import React, { FC, useRef, useState, type MutableRefObject } from 'react';
import Image from 'next/image';
import {
  MecImage,
  ProductImageType,
} from '@adapters/bigcommerce/types/product';
import Tag from '_components/UI/Tag';
import { PDP_GALLERY_BADGE_OVERLAY_SELECTOR } from '_components/UI/CanadianBadge/constants';
import {
  GalleryImageFitMode,
  getGalleryImageFitFromImageType,
} from '../constants';
import {
  getGalleryImageAspectRatio,
  getGalleryImageFitFromAspectRatio,
} from '../galleryImageAspect';
import type { GalleryPointerDragSession } from '../galleryPointerDrag';
import styles from './ImageContainer.module.scss';
/**
 * Legacy env name: when enabled, gallery uses ratio/type-based fit selection.
 * Set to false to force all gallery images to contain (safe rollback).
 */
const TYPE_BASED_GALLERY_IMAGE_FIT_ENABLED =
  process.env.NEXT_PUBLIC_PDP_ADAPTIVE_IMAGE_FIT !== 'false';

function isGalleryOverlayControlTarget(
  eventTarget: EventTarget | null
): boolean {
  if (!eventTarget) {
    return false;
  }
  const node = eventTarget as Node;
  if (node.nodeType === Node.ELEMENT_NODE) {
    return Boolean(
      (node as Element).closest(PDP_GALLERY_BADGE_OVERLAY_SELECTOR)
    );
  }
  if (node.nodeType === Node.TEXT_NODE) {
    return Boolean(
      (node as Text).parentElement?.closest(PDP_GALLERY_BADGE_OVERLAY_SELECTOR)
    );
  }
  return false;
}

export interface ImageContainerProps {
  image: MecImage;
  isPriority?: boolean;
  sizes: string;
  /**
   * Optional override for object-fit behavior.
   * When provided, bypasses type-based fit (PRI/ALT contain, LFS cover) and env toggles.
   */
  imageFitMode?: GalleryImageFitMode;
  /** When set (e.g. borrowed ALT + `product.colour`), overrides `image.tag` for the overlay. */
  tagDisplay?: string;
  /** Aspect ratio of the gallery canvas for this cell, used by auto fit selection. */
  canvasAspectRatio?: number;
  /** QA-only override for the auto-fit cover range threshold. */
  coverRangeOverride?: number;
  /**
   * When true, forces non-LFS images to `contain` regardless of aspect ratio.
   * Evaluated after `imageFitMode`, `TYPE_BASED_GALLERY_IMAGE_FIT_ENABLED`, and
   * `isPriority` — those guards all take precedence over this flag.
   */
  isNarrowViewport?: boolean;
  /**
   * When true, skips the 16px gallery inset (e.g. placeholder / no-image treatment).
   * LFS images always omit the inset regardless of this flag.
   */
  omitGalleryImageInset?: boolean;
  children?: React.ReactNode;
}

interface ActivatableImageContainerProps extends ImageContainerProps {
  pointerDragSessionRef: MutableRefObject<GalleryPointerDragSession>;
  onActivate: (options: {
    trigger?: HTMLButtonElement | null;
    source: 'keyboard' | 'mouse' | 'touch';
  }) => void;
  activateAriaLabel: string;
  testId?: string;
}

const ImageContainerContent: FC<ImageContainerProps> = ({
  image,
  isPriority = false,
  sizes,
  imageFitMode,
  tagDisplay,
  canvasAspectRatio,
  coverRangeOverride,
  isNarrowViewport = false,
  omitGalleryImageInset = false,
  children,
}) => {
  const overlayTag = tagDisplay ?? image.tag;
  const [naturalDimensions, setNaturalDimensions] = useState<{
    width?: number;
    height?: number;
  }>({});
  const effectiveWidth = image.width ?? naturalDimensions.width;
  const effectiveHeight = image.height ?? naturalDimensions.height;
  const ratio = getGalleryImageAspectRatio(effectiveWidth, effectiveHeight);

  const getImageFitMode = (): GalleryImageFitMode => {
    if (imageFitMode) return imageFitMode;

    if (!TYPE_BASED_GALLERY_IMAGE_FIT_ENABLED) return 'contain';

    if (isPriority) {
      return 'contain';
    }

    if (image.imageType === ProductImageType.LFS) {
      return 'cover';
    }

    if (isNarrowViewport) {
      return 'contain';
    }

    const ratioBasedFitMode = getGalleryImageFitFromAspectRatio({
      imageRatio: ratio,
      canvasRatio: canvasAspectRatio,
      coverRange: coverRangeOverride,
    });

    if (typeof ratio === 'number' && typeof canvasAspectRatio === 'number') {
      return ratioBasedFitMode;
    }

    return getGalleryImageFitFromImageType(image);
  };

  const fitMode = getImageFitMode();
  const shouldOmitGalleryImageInset =
    omitGalleryImageInset ||
    image.imageType === ProductImageType.LFS ||
    fitMode !== 'contain';
  const imageClassName = [
    styles.image,
    fitMode === 'contain' ? styles.imageContain : '',
    !shouldOmitGalleryImageInset ? styles.galleryImageInset : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <Image
        src={image.urlOriginal}
        alt={image.altText || ''}
        fill
        data-gallery-image-fit={fitMode}
        className={imageClassName}
        draggable={false}
        priority={isPriority}
        sizes={sizes}
        onLoad={(event) => {
          const currentTarget = event.currentTarget as HTMLImageElement | null;
          if (!currentTarget || typeof window === 'undefined') {
            return;
          }

          setNaturalDimensions({
            width: currentTarget.naturalWidth || undefined,
            height: currentTarget.naturalHeight || undefined,
          });
        }}
      />
      {children}
      {overlayTag && (
        <div className={styles.imageTag} aria-hidden="true">
          <Tag size="medium" onDarkBg>
            {overlayTag}
          </Tag>
        </div>
      )}
    </>
  );
};

/**
 * ImageContainer wraps an image with its container that handles aspect ratios and layout.
 * Used within ImageGallery to render individual product images.
 *
 * Features:
 * - Responsive aspect ratios (mobile: square, desktop: first child square, others portrait)
 * - Accepts children for overlays (e.g., badges)
 * - Configurable image priority for performance
 * - Next.js Image optimization with custom sizes
 */
const ImageContainer: FC<ImageContainerProps> = ({
  image,
  isPriority = false,
  sizes,
  imageFitMode,
  tagDisplay,
  canvasAspectRatio,
  coverRangeOverride,
  isNarrowViewport,
  omitGalleryImageInset,
  children,
}) => {
  return (
    <div className={styles.imageWrapper}>
      <ImageContainerContent
        image={image}
        isPriority={isPriority}
        sizes={sizes}
        imageFitMode={imageFitMode}
        tagDisplay={tagDisplay}
        canvasAspectRatio={canvasAspectRatio}
        coverRangeOverride={coverRangeOverride}
        isNarrowViewport={isNarrowViewport}
        omitGalleryImageInset={omitGalleryImageInset}
      >
        {children}
      </ImageContainerContent>
    </div>
  );
};

export const ActivatableImageContainer: FC<ActivatableImageContainerProps> = ({
  image,
  isPriority = false,
  sizes,
  imageFitMode,
  tagDisplay,
  canvasAspectRatio,
  coverRangeOverride,
  isNarrowViewport,
  omitGalleryImageInset,
  children,
  pointerDragSessionRef,
  onActivate,
  activateAriaLabel,
  testId,
}) => {
  const activatorRef = useRef<HTMLButtonElement | null>(null);
  const suppressClickRef = useRef(false);

  return (
    <div
      className={styles.imageWrapper}
      onPointerDown={(event) => {
        if (!event.isPrimary) return;
        if (isGalleryOverlayControlTarget(event.target)) {
          return;
        }
        const s = pointerDragSessionRef.current;
        s.activePointerId = event.pointerId;
        s.activePointerType = event.pointerType;
        s.dragStartPos = { x: event.clientX, y: event.clientY };
        s.hasDragged = false;
        suppressClickRef.current = false;
      }}
      onPointerUpCapture={(event) => {
        const s = pointerDragSessionRef.current;
        if (s.activePointerId !== event.pointerId) {
          return;
        }

        const pointerType = s.activePointerType;
        const dragged = s.hasDragged;
        s.activePointerId = null;
        s.activePointerType = null;
        s.dragStartPos = null;

        if (isGalleryOverlayControlTarget(event.target)) {
          return;
        }

        if (pointerType === 'mouse' || dragged) {
          return;
        }

        suppressClickRef.current = true;
        onActivate({
          trigger: activatorRef.current,
          source: 'touch',
        });
      }}
      onClick={(event) => {
        if (event.detail === 0) {
          return;
        }

        if (isGalleryOverlayControlTarget(event.target)) {
          return;
        }

        if (suppressClickRef.current) {
          event.preventDefault();
          event.stopPropagation();
          suppressClickRef.current = false;
          return;
        }

        const s = pointerDragSessionRef.current;
        if (s.hasDragged) {
          event.preventDefault();
          event.stopPropagation();
          s.hasDragged = false;
          return;
        }

        onActivate({
          trigger: activatorRef.current,
          source: 'mouse',
        });
      }}
    >
      <button
        type="button"
        ref={activatorRef}
        className={styles.imageActivator}
        onClick={(event) => {
          if (event.detail === 0) {
            onActivate?.({
              trigger: event.currentTarget,
              source: 'keyboard',
            });
          }
        }}
        aria-label={activateAriaLabel}
        data-testid={testId}
      />
      <ImageContainerContent
        image={image}
        isPriority={isPriority}
        sizes={sizes}
        imageFitMode={imageFitMode}
        tagDisplay={tagDisplay}
        canvasAspectRatio={canvasAspectRatio}
        coverRangeOverride={coverRangeOverride}
        isNarrowViewport={isNarrowViewport}
        omitGalleryImageInset={omitGalleryImageInset}
      >
        {children}
      </ImageContainerContent>
    </div>
  );
};

export default ImageContainer;
